// src/components/Header/Header.tsx
// Dynamic Navbar with integrated breadcrumbs — Google-inspired premium design

import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Home, ChevronRight } from 'lucide-react';
import { m, AnimatePresence } from 'framer-motion';
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { useBreadcrumbs } from '../../hooks/useBreadcrumbs';

interface NavItem {
    label: string;
    path: string;
}

const Header: React.FC = () => {
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [headerY, setHeaderY] = useState(0);
    const lastScrollYRef = useRef(0);
    const ticking = useRef(false);

    const { crumbs, depth } = useBreadcrumbs();

    // Show breadcrumbs when depth >= 2 (sub-pages)
    const showBreadcrumbs = depth >= 2;

    // SMART HIDE & ELEVATION: Reactive transparency + Scroll Sync logic
    useEffect(() => {
        const updateHeader = () => {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - lastScrollYRef.current;

            // 1. Handle Elevation (Transparency)
            setScrolled(currentScrollY > 10);

            // 2. Hybrid Scroll-Sync Logic (Antigravity Standard)
            const threshold = 5;
            const targetHeight = 48; // Standard Header Height matching h-12

            if (currentScrollY < threshold) {
                // Pin to top at start
                setHeaderY(0);
            } else if (delta > 0) {
                // Scrolling Down: Push away at aggressive 1.8x speed
                if (!isMenuOpen) {
                    setHeaderY(prev => Math.max(-targetHeight, prev - delta * 1.8));
                }
            } else if (delta < 0) {
                // Scrolling Up: Immediate reveal (Antigravity frictionless)
                setHeaderY(0);
            }

            lastScrollYRef.current = currentScrollY;
            ticking.current = false;
        };

        const onScroll = () => {
            if (!ticking.current) {
                window.requestAnimationFrame(updateHeader);
                ticking.current = true;
            }
        };

        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, [isMenuOpen]);

    // AUTO-CLOSE: If the window is resized to desktop width while menu is open, close it.
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 1024 && isMenuOpen) {
                setIsMenuOpen(false);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [isMenuOpen]);

    const isActive = (path: string): boolean => {
        if (path === '/') return location.pathname === '/';
        return location.pathname.startsWith(path);
    };

    const coreNavItems: NavItem[] = [
        { label: 'Tools', path: '/tools' },
        { label: 'Developer Hub', path: '/dev-hub' },
        { label: 'Insights', path: '/insights' },
    ];

    const secondaryNavItems: NavItem[] = [
        { label: 'About Us', path: '/about' },
        { label: 'Contact', path: '/contact' },
    ];

    const allNavItems: NavItem[] = [
        ...coreNavItems,
        ...secondaryNavItems
    ];

    /**
     * Renders the full breadcrumb trail for the center area.
     * 
     * Design rules:
     * - First crumb = Home icon (no text) 
     * - Separators = ChevronRight, 12px, low opacity
     * - Intermediate crumbs = subtle pill hover effect
     * - Last crumb = Google Blue, medium weight
     */
    const renderBreadcrumbs = () => {
        if (crumbs.length === 0) return null;

        return (
            <nav
                className="flex items-center gap-0 h-full overflow-hidden"
                aria-label="Breadcrumb"
            >
                <ol className="flex items-center gap-0 list-none p-0 m-0">
                    {crumbs.map((crumb, index) => {
                        const isFirst = index === 0;
                        const isLast = index === crumbs.length - 1;

                        return (
                            <li key={crumb.path} className="flex items-center">
                                {/* Separator before non-first items */}
                                {!isFirst && (
                                    <ChevronRight
                                        size={12}
                                        className="mx-1 text-[#9aa0a6] shrink-0"
                                        strokeWidth={2}
                                    />
                                )}

                                {isFirst ? (
                                    /* Home icon crumb */
                                    <Link
                                        to={crumb.path}
                                        className="flex items-center justify-center w-7 h-7 rounded-full text-[#5f6368] hover:bg-[#f1f3f4] hover:text-[#202124] transition-all duration-200"
                                        title="Home"
                                    >
                                        <Home size={15} strokeWidth={1.8} />
                                    </Link>
                                ) : isLast ? (
                                    /* Current page — highlighted */
                                    <span
                                        className="text-[13px] font-medium text-[#1a73e8] leading-none whitespace-nowrap"
                                    >
                                        {crumb.label}
                                    </span>
                                ) : (
                                    /* Intermediate link crumb — pill hover */
                                    <Link
                                        to={crumb.path}
                                        className="text-[13px] text-[#5f6368] hover:text-[#202124] hover:bg-[#f1f3f4] px-2 py-1 rounded-full transition-all duration-200 no-underline whitespace-nowrap leading-none"
                                    >
                                        {crumb.label}
                                    </Link>
                                )}
                            </li>
                        );
                    })}
                </ol>
            </nav>
        );
    };

    // Generate JSON-LD BreadcrumbList schema for SEO
    const breadcrumbSchema = crumbs.length > 0 ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": crumbs.map((crumb, index) => ({
            "@type": "ListItem",
            "position": index + 1,
            "name": crumb.label,
            "item": `https://filefusion.app${crumb.path}`
        }))
    } : null;

    return (
        <>
            {/* SEO: Breadcrumb structured data */}
            {breadcrumbSchema && (
                <script type="application/ld+json">
                    {JSON.stringify(breadcrumbSchema)}
                </script>
            )}

            <m.header
                animate={{
                    y: headerY,
                }}
                transition={{
                    duration: 0.15,
                    ease: "easeOut"
                }}
                className={`fixed top-0 left-0 right-0 z-[100] h-12 flex items-center justify-center transition-[background-color] duration-300 ${isMenuOpen || scrolled
                    ? 'bg-background font-brand'
                    : 'bg-transparent'
                    }`}
            >
                <div className="w-full max-w-6xl FF-indent flex items-center justify-between mx-auto relative h-full">

                    {/* 1. Brand Identity (Logo Section) */}
                    <div className="flex-shrink-0 z-10 flex items-center h-full">
                        <Link
                            to="/"
                            onClick={() => setIsMenuOpen(false)}
                            className="flex items-center gap-2 group transition-colors active:scale-[0.98] cursor-pointer"
                        >
                            <img
                                src="/assets/logos/filefusion-icon-only.svg"
                                alt="FileFusion"
                                width={24}
                                height={24}
                                className="w-6 h-6 shrink-0"
                            />
                            <span className="text-lg font-bold font-brand tracking-tight text-foreground transition-colors group-hover:text-primary leading-none">
                                FileFusion
                            </span>
                        </Link>
                    </div>

                    {/* 2. Center: Dynamic Navigation / Breadcrumbs */}
                    <AnimatePresence mode="wait">
                        {showBreadcrumbs ? (
                            /* Breadcrumb Mode — replaces center nav on sub-pages */
                            <m.div
                                key="breadcrumbs"
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center h-full"
                            >
                                {renderBreadcrumbs()}
                            </m.div>
                        ) : (
                            /* Standard Nav — for root and top-level pages */
                            <m.nav
                                key="nav-links"
                                initial={{ opacity: 0, y: 4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -4 }}
                                transition={{ duration: 0.2, ease: 'easeOut' }}
                                className="hidden lg:flex absolute left-1/2 -translate-x-1/2 items-center gap-10 h-full"
                                aria-label="Core navigation"
                            >
                                {coreNavItems.map((item) => (
                                    <Link
                                        key={item.path}
                                        to={item.path}
                                        className={`relative py-1 text-[15px] font-medium transition-colors hover:text-primary cursor-pointer ${isActive(item.path) ? 'text-primary' : 'text-foreground/70'
                                            }`}
                                    >
                                        {item.label}
                                    </Link>
                                ))}
                            </m.nav>
                        )}
                    </AnimatePresence>

                    {/* 3. Right: Secondary Links & Toggle Group */}
                    <div className="flex items-center gap-8 z-10 h-full ml-auto">
                        {/* Desktop Right Nav (Text-Only) */}
                        <nav className="hidden lg:flex items-center gap-8 h-full" aria-label="Secondary navigation">
                            {secondaryNavItems.map((item) => (
                                <Link
                                    key={item.path}
                                    to={item.path}
                                    className={`relative py-1 text-[15px] font-medium transition-colors hover:text-primary cursor-pointer ${isActive(item.path) ? 'text-primary' : 'text-foreground/70'
                                        }`}
                                >
                                    {item.label}
                                </Link>
                            ))}
                        </nav>

                        {/* Mobile Menu Toggle - Strictly Hidden on Desktop (Restored logic) */}
                        <div className="lg:hidden flex items-center h-full">
                            <Sheet open={isMenuOpen} onOpenChange={setIsMenuOpen} modal={false}>
                                <SheetTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        aria-label={isMenuOpen ? "Close menu" : "Open menu"}
                                        className="rounded-full w-10 h-10 transition-all text-[#5f6368] hover:bg-[#1a73e8]/10 hover:text-[#1a73e8] active:scale-95 cursor-pointer flex items-center justify-center shrink-0"
                                    >
                                        {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                                    </Button>
                                </SheetTrigger>

                                <SheetContent
                                    side="right"
                                    className="w-full sm:max-w-md p-0 gap-0 top-12 h-[calc(100vh-3rem)] border-t-0 border-l border-[#dadce0]/10 shadow-none bg-white/90 backdrop-blur-2xl animate-none overflow-hidden"
                                    showCloseButton={false}
                                >
                                    <nav className="flex flex-col h-full mt-4">
                                        {/* Mobile Breadcrumb Strip — only on sub-pages */}
                                        {showBreadcrumbs && crumbs.length > 0 && (
                                            <div className="px-6 pb-4 border-b border-[#dadce0]/15">
                                                <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide py-1">
                                                    {crumbs.map((crumb, index) => {
                                                        const isLast = index === crumbs.length - 1;
                                                        return (
                                                            <React.Fragment key={crumb.path}>
                                                                {index > 0 && (
                                                                    <ChevronRight
                                                                        size={11}
                                                                        className="text-[#9aa0a6] shrink-0 mx-0.5"
                                                                        strokeWidth={2}
                                                                    />
                                                                )}
                                                                {index === 0 ? (
                                                                    <Link
                                                                        to={crumb.path}
                                                                        onClick={() => setIsMenuOpen(false)}
                                                                        className="shrink-0"
                                                                    >
                                                                        <Home size={13} className="text-[#5f6368]" strokeWidth={1.8} />
                                                                    </Link>
                                                                ) : isLast ? (
                                                                    <span className="text-[12px] font-medium text-[#1a73e8] whitespace-nowrap truncate max-w-[140px]">
                                                                        {crumb.label}
                                                                    </span>
                                                                ) : (
                                                                    <Link
                                                                        to={crumb.path}
                                                                        onClick={() => setIsMenuOpen(false)}
                                                                        className="text-[12px] text-[#5f6368] whitespace-nowrap no-underline hover:text-[#1a73e8] transition-colors"
                                                                    >
                                                                        {crumb.label}
                                                                    </Link>
                                                                )}
                                                            </React.Fragment>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex-1 overflow-y-auto pt-4">
                                            <AnimatePresence mode="wait">
                                                {allNavItems.map((item, index) => (
                                                    <m.div
                                                        key={item.path}
                                                        initial={{ opacity: 0, x: -10 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        exit={{ opacity: 0, x: 10 }}
                                                        transition={{
                                                            duration: 0.3,
                                                            delay: index * 0.05 + 0.1,
                                                            ease: [0.22, 1, 0.36, 1]
                                                        }}
                                                        className="flex items-center justify-between h-[60px] w-full border-b border-[#dadce0]/10 flex-shrink-0 px-8"
                                                    >
                                                        <Link
                                                            to={item.path}
                                                            onClick={() => setIsMenuOpen(false)}
                                                            className="flex items-center h-full w-full"
                                                        >
                                                            <span className={`text-xl tracking-tight cursor-pointer transition-colors hover:text-[#1a73e8] ${isActive(item.path) ? 'text-[#1a73e8] font-medium' : 'text-[#202124]/70 font-medium'
                                                                }`}>
                                                                {item.label}
                                                            </span>
                                                        </Link>
                                                        {isActive(item.path) && (
                                                            <m.div
                                                                layoutId="mobile-indicator"
                                                                className="w-2 h-2 rounded-full bg-[#1a73e8]"
                                                                initial={{ scale: 0 }}
                                                                animate={{ scale: 1 }}
                                                            />
                                                        )}
                                                    </m.div>
                                                ))}
                                            </AnimatePresence>
                                        </div>
                                    </nav>
                                </SheetContent>
                            </Sheet>
                        </div>
                    </div>
                </div>
            </m.header>
        </>
    );
};

export default Header;
